import { useOddsStore } from '../../stores/oddsStore'
import { useOddsDirectionFlash } from '../../hooks/useOddsDirectionFlash'
import { calculateParlayOdds, combinedDecimalOdds, decimalOddsOf, formatOdds } from '../../utils/odds'
import {
  getSelections,
  getPrimaryEntry,
  selectionOdds,
  selectionSetBetTypeDisplayLabel,
  selectionSetBetTypeLabel,
  selectionSetCombinedOdds,
  selectionSetHasCombinedOdds,
  selectionSetHasCorrelatedLegs,
  selectionSetIsSameGameParlay,
  selectionSetIsMultipleSingles,
} from '../../utils/selectionSet'
import { CardTags } from './CardTags'
import { SgpBadge } from './SgpBadge'
import type { FeedTag, SelectionSet } from '../../types/feedContract'
import { useFeedConfig } from '../../contexts/feedConfigContext'
import { useI18n } from '../../i18n'
import { useOddsFormat } from '../../i18n/hostFormat'

interface CardBetTopLineProps {
  selectionSet: SelectionSet
  postId: string
  tags?: FeedTag[]
}


export function CardBetTopLine({ selectionSet, postId, tags }: CardBetTopLineProps) {
  const { branding } = useFeedConfig()
  const { t } = useI18n()
  const oddsFormat = useOddsFormat()
  const presentation = branding.presentation.betslip
  const oddsData = useOddsStore((s) => s.getOddsForActivity(postId))
  const selections = getSelections(selectionSet)

  const selectionSetLabel = selectionSetBetTypeLabel(selectionSet)
  const hasCombinedOdds = selectionSetHasCombinedOdds(selectionSet)
  const showCombinedOdds = hasCombinedOdds && selections.length > 1
  const authoredCombinedOdds = selectionSetCombinedOdds(selectionSet)
  const isSameGameParlay = selectionSetIsSameGameParlay(selectionSet)
  const isMultipleSingles = selectionSetIsMultipleSingles(selectionSet)
  let displayLabel = selectionSetBetTypeDisplayLabel(selectionSet, t)
  if (isSameGameParlay && presentation.labelTemplate) {
    displayLabel = presentation.labelTemplate.replaceAll('{count}', String(selections.length))
  } else if (
    !isMultipleSingles
    && selectionSetLabel === 'parlay'
    && selections.length > 1
    && presentation.parlayLabelTemplate
  ) {
    displayLabel = presentation.parlayLabelTemplate.replaceAll('{count}', String(selections.length))
  }
  const showDisplayLabel = selections.length > 1 || presentation.showSingleLabel
  const primaryEntry = getPrimaryEntry(selectionSet)
  const priceKind = selectionSet.summary?.price_kind || primaryEntry?.price_kind
  const validUntil = selectionSet.summary?.valid_until || primaryEntry?.valid_until
  const validUntilTime = validUntil ? Date.parse(validUntil) : Number.NaN
  const isExpired = Number.isFinite(validUntilTime) && validUntilTime <= Date.now()
  const sameGameGroupLabel = selectionSet.groups?.find(
    (group) => group.kind === 'same_game' && typeof group.label === 'string' && group.label,
  )?.label
  const firstEventName = selections[0]?.event?.name
  const eventLabel: string | undefined = isSameGameParlay
    ? typeof sameGameGroupLabel === 'string'
      ? sameGameGroupLabel
      : typeof firstEventName === 'string' ? firstEventName : undefined
    : undefined

  let displayOdds: string | null = null
  if (showCombinedOdds) {
    let candidateOdds = ''
    if (oddsData?.updatedOdds) {
      candidateOdds = formatOdds({ american: oddsData.updatedOdds, decimal: oddsData.updatedDecimal }, oddsFormat)
    } else if (authoredCombinedOdds) {
      candidateOdds = formatOdds(authoredCombinedOdds, oddsFormat)
    } else if (!selectionSetHasCorrelatedLegs(selectionSet)) {
      // Correlated legs are priced by the provider; multiplying them here
      // would fabricate a combined price, so the top line waits for the
      // live quote instead.
      const staticOdds = selections.map((selection) => formatOdds(selectionOdds(selection)))
      if (staticOdds.length > 0 && staticOdds.every((odds) => odds !== '--')) {
        const american = calculateParlayOdds(staticOdds)
        const decimal = combinedDecimalOdds(selections.map((selection) => decimalOddsOf(selectionOdds(selection))))
        candidateOdds = oddsFormat === 'decimal' ? formatOdds({ american, decimal }, 'decimal') : american
      }
    }
    if (candidateOdds && candidateOdds !== '--') displayOdds = candidateOdds
  }

  const { flashKey, direction, clearFlash } = useOddsDirectionFlash(
    displayOdds,
    showCombinedOdds && Boolean(oddsData?.updatedOdds),
    oddsFormat,
  )

  const odds = displayOdds ? (
    <span
      key={flashKey}
      className={`card-bet-odds${direction ? ` odds-flash-${direction}` : ''}`}
    >
      {displayOdds}
      {direction === 'increased' && (
        <span
          className="odds-arrow odds-arrow-up"
          aria-hidden="true"
          onAnimationEnd={clearFlash}
        >
          &#9650;
        </span>
      )}
      {direction === 'decreased' && (
        <span
          className="odds-arrow odds-arrow-down"
          aria-hidden="true"
          onAnimationEnd={clearFlash}
        >
          &#9660;
        </span>
      )}
    </span>
  ) : null

  const badge = isSameGameParlay ? <SgpBadge /> : null
  const showEvent = presentation.showToplineEvent && Boolean(eventLabel)
  const hasVisibleContent = showDisplayLabel
    || Boolean(badge)
    || Boolean(odds)
    || priceKind === 'derived'
    || isExpired
    || Boolean(tags?.length)
    || showEvent

  if (!hasVisibleContent) return null

  return (
    <div className="card-bet-topline">
      <div className="card-bet-meta">
        {presentation.badgePosition === 'before-label' ? badge : null}
        {showDisplayLabel ? <span className="card-bet-type">{displayLabel}</span> : null}
        {presentation.badgePosition !== 'before-label' ? badge : null}
        {priceKind === 'derived' && (
          <span className="card-bet-price-kind">{t('bet.estimated')}</span>
        )}
        {isExpired && (
          <span className="card-bet-freshness card-bet-freshness-expired">{t('bet.expiredBadge')}</span>
        )}
        {tags && tags.length > 0 && (
          <CardTags tags={tags} className="card-tags-betline" />
        )}
        {presentation.oddsPosition === 'inline' ? odds : null}
      </div>
      {showEvent && eventLabel
        ? <span className="card-bet-event">{eventLabel}</span>
        : null}
      {presentation.oddsPosition !== 'inline' ? odds : null}
    </div>
  )
}
